import { useState } from "react";
import { KOREA_PATH, KOREA_VIEWBOX, projectLonLat } from "./korea-path";
import type { Lang } from "@/lib/useLang";

// 서비스 지역 지도: 남한 외곽선 위에 방선 지원 항만을 표시. 부산 본사에서 각 항만으로 이어지는 점선과 파동 효과
// anchor: 글자 정렬(동해안은 오른쪽, 서·남해안은 왼쪽, 남해안 중간은 아래 가운데), dy: 세로 보정
const PORTS: { ko: string; en: string; lon: number; lat: number; hq?: boolean; anchor?: "start" | "end" | "middle"; dy?: number }[] = [
  { ko: "부산", en: "Busan", lon: 129.04, lat: 35.1, hq: true, anchor: "start", dy: 22 },
  { ko: "울산", en: "Ulsan", lon: 129.31, lat: 35.54, anchor: "start" },
  { ko: "포항", en: "Pohang", lon: 129.37, lat: 36.03, anchor: "start" },
  { ko: "삼척", en: "Samcheok", lon: 129.17, lat: 37.45, anchor: "start", dy: 20 },
  { ko: "동해", en: "Donghae", lon: 129.11, lat: 37.52, anchor: "start", dy: -6 },
  { ko: "마산", en: "Masan", lon: 128.57, lat: 35.2, anchor: "middle", dy: 26 },
  { ko: "광양", en: "Gwangyang", lon: 127.7, lat: 34.94, anchor: "end", dy: -8 },
  { ko: "여수", en: "Yeosu", lon: 127.66, lat: 34.76, anchor: "end", dy: 18 },
  { ko: "목포", en: "Mokpo", lon: 126.39, lat: 34.79, anchor: "end" },
  { ko: "군산", en: "Gunsan", lon: 126.71, lat: 35.98, anchor: "end" },
  { ko: "보령", en: "Boryeong", lon: 126.5, lat: 36.35, anchor: "end" },
  { ko: "서산", en: "Seosan", lon: 126.45, lat: 36.78, anchor: "end" },
  { ko: "평택", en: "Pyeongtaek", lon: 126.83, lat: 36.97, anchor: "start", dy: 20 },
  { ko: "인천", en: "Incheon", lon: 126.63, lat: 37.46, anchor: "end" },
];

export default function KoreaMap({ lang, hqLabel, portLabel }: { lang: Lang; hqLabel: string; portLabel: string }) {
  const [active, setActive] = useState<number | null>(null);
  const hq = PORTS.find((p) => p.hq)!;
  const hqXY = projectLonLat(hq.lon, hq.lat);

  return (
    <svg viewBox={KOREA_VIEWBOX} className="w-full h-full" role="img" aria-label={lang === "ko" ? "국내 방선 지원 항만 지도" : "Map of supported Korean ports"}>
      <defs>
        <linearGradient id="land" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="oklch(var(--foreground-700))" />
          <stop offset="1" stopColor="oklch(var(--foreground-800))" />
        </linearGradient>
      </defs>
      <path d={KOREA_PATH} fill="url(#land)" stroke="oklch(var(--accent-400) / 0.45)" strokeWidth="1.2" strokeLinejoin="round" />

      {/* 본사 → 항만 점선 */}
      {PORTS.filter((p) => !p.hq).map((p) => {
        const { x, y } = projectLonLat(p.lon, p.lat);
        return (
          <line
            key={p.en}
            x1={hqXY.x}
            y1={hqXY.y}
            x2={x}
            y2={y}
            className="route-line"
            stroke="oklch(var(--accent-300) / 0.55)"
            strokeWidth="1.5"
          />
        );
      })}

      {PORTS.map((p, i) => {
        const { x, y } = projectLonLat(p.lon, p.lat);
        const isActive = active === i;
        const dx = p.anchor === "end" ? -14 : p.anchor === "middle" ? 0 : 14;
        return (
          <g
            key={p.en}
            className="cursor-pointer"
            onPointerEnter={() => setActive(i)}
            onPointerLeave={() => setActive(null)}
            onClick={() => setActive(isActive ? null : i)}
          >
            <circle cx={x} cy={y} r={p.hq ? 10 : 8} fill="oklch(var(--accent-400) / 0.9)" className="port-pulse" style={{ animationDelay: `${i * 0.35}s` }} />
            <circle cx={x} cy={y} r={p.hq ? 7 : 5} fill={p.hq ? "var(--brand-orange)" : "oklch(var(--accent-300))"} stroke="oklch(var(--foreground-950))" strokeWidth="2" />
            <text
              x={x + dx}
              y={y + (p.dy ?? 5)}
              textAnchor={p.anchor}
              fontSize="20"
              fontWeight={700}
              fill={isActive ? "oklch(var(--background-50))" : "oklch(var(--background-200))"}
              className="font-heading select-none"
            >
              {lang === "ko" ? p.ko : p.en}
            </text>
            {isActive && (
              <text x={x + dx} y={y + (p.dy ?? 5) + 24} textAnchor={p.anchor} fontSize="16" fill="oklch(var(--accent-300))" className="select-none">
                {p.hq ? hqLabel : portLabel}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
